import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getJobById, updateJob, deleteJob } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Briefcase,
  DollarSign,
  Phone,
  Save,
  AlertCircle,
  Clock,
  Mail,
  FileText,
  Star,
  Trash2,
} from "lucide-react";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { AudienceSelect, toAudience, type Audience } from "../../client/components/AudienceSelect";

interface JobFormData {
  title: string;
  description: string;
  listingType: "OFFER" | "REQUEST";
  price: string;
  priceType: "HOURLY" | "FIXED" | "PER_SESSION" | "FREE";
  schedule: string;
  contactEmail: string;
  contactPhone: string;
}

const LISTING_TYPE_OPTIONS = [
  { value: "OFFER", label: "Offering", icon: Briefcase },
  { value: "REQUEST", label: "Requesting", icon: Star },
];

const PRICE_TYPE_OPTIONS = [
  { value: "HOURLY", label: "Hourly" },
  { value: "FIXED", label: "Fixed" },
  { value: "PER_SESSION", label: "Per Session" },
  { value: "FREE", label: "Free" },
];

export default function EditJobPage() {
  useRequireSectionVisible("showJob");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const {
    data: job,
    isLoading,
    error,
  } = useQuery(getJobById, { itemId: itemId! });
  const updateJobFn = useAction(updateJob);
  const deleteJobFn = useAction(deleteJob);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [audience, setAudience] = useState<Audience>("VERIFIED");
  
  const [formData, setFormData] = useState<JobFormData>({
    title: "",
    description: "",
    listingType: "OFFER",
    price: "",
    priceType: "FIXED",
    schedule: "",
    contactEmail: "",
    contactPhone: "",
  });

  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || "",
        description: job.description || "",
        listingType: (job.listingType as any) || "OFFER",
        price: job.price?.toString() || "",
        priceType: (job.priceType as any) || "FIXED",
        schedule: job.schedule || "",
        contactEmail: job.email || "",
        contactPhone: job.phone || "",
      });
      setAudience(toAudience(job.visibility));
    }
  }, [job]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof JobFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Job title is required";
    if (!formData.description.trim()) newErrors.description = "Job description is required";
    if (formData.priceType !== "FREE" && !formData.price) newErrors.price = "Price is required";
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    if (formData.contactPhone && formData.contactPhone?.replace(/\D/g, "").length < 10) {
      newErrors.contactPhone = "Please enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await updateJobFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        listingType: formData.listingType,
        price: formData.priceType === "FREE" ? 0 : parseFloat(formData.price),
        priceType: formData.priceType,
        schedule: formData.schedule.trim(),
        email: formData.contactEmail || undefined,
        phone: formData.contactPhone || undefined,
        visibility: audience,
      });

      handleGoBack();
    } catch (error) {
      console.error("Error updating job:", error);
      setErrors({ submit: "Failed to update job. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this job post? This action cannot be undone.")) return;

    try {
      await deleteJobFn({ itemId: itemId! });
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/jobs`);
      } else {
        navigate("/jobs");
      }
    } catch (error) {
      console.error("Error deleting job:", error);
      setErrors({ submit: "Failed to delete job. Please try again." });
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/jobs`);
    } else {
      navigate("/jobs");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to edit job posts.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Job...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (error || !job) {
    return (
      <IndividualEditPageLayout title="Job Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Job Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The job post you're looking for doesn't exist.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="job-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={() => navigate(-1)}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Actions">
        <button
          type="button"
          onClick={handleDelete}
          className="w-full px-4 py-3 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all active:scale-[0.98]"
        >
          <Trash2 className="w-4 h-4" />
          Delete Post
        </button>
      </SidebarSection>

      <SidebarSection title="Information" hideOnMobile>
        <div className="space-y-4">
          <div className="flex gap-3 text-sm">
            <div className="mt-1 p-1 bg-blue-50 rounded-md text-blue-600">
              <AlertCircle className="w-4 h-4" />
            </div>
            <p className="text-slate-600">
              Changes will be updated immediately in <strong>{selectedLocation?.name}</strong>.
            </p>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Job"
      subtitle={job.title}
      icon={Briefcase}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="job-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Listing Type & Title */}
        <div className="space-y-6">
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-900 ml-1">
              Purpose
            </label>
            <div className="inline-flex p-1 bg-slate-100 rounded-xl w-full max-w-md">
              {LISTING_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChange("listingType", opt.value)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    formData.listingType === opt.value
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <opt.icon className="w-4 h-4" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 ml-1">Job Title</label>
            <div className="relative group">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g. Graphic Designer, Math Tutor, Delivery Help"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                required
              />
            </div>
          </div>
        </div>

        {/* Pricing & Schedule */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Compensation & Schedule</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700 ml-1">Payment Type</label>
              <div className="inline-flex p-1 bg-slate-100 rounded-xl w-full">
                {PRICE_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange("priceType", opt.value)}
                    className={`flex-1 py-2 px-1 rounded-lg text-xs font-semibold transition-all ${
                      formData.priceType === opt.value
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5 pt-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Amount</label>
              <div className="relative group">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  disabled={formData.priceType === "FREE"}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder={formData.priceType === "FREE" ? "No charge" : "0.00"}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-50 disabled:bg-slate-100"
                />
              </div>
              {errors.price && <p className="text-xs text-rose-500 mt-1 ml-1">{errors.price}</p>}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 ml-1">Typical Schedule / Time Commit</label>
            <div className="relative group">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                value={formData.schedule}
                onChange={(e) => handleChange("schedule", e.target.value)}
                placeholder="e.g. Weekends only, 5-10 hours/week, or Flexible"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Job Details</span>
          </div>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Provide a detailed description of the job, requirements, and what you're looking for..."
            rows={5}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none"
            required
          />
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-slate-900 border-b border-slate-100 pb-2">
            <Mail className="w-4 h-4 text-blue-600" />
            <span className="font-semibold">Contact Information</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Email</label>
              <div className="relative bg-slate-100 rounded-xl cursor-not-allowed">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={formData.contactEmail}
                  readOnly
                  disabled
                  className="w-full pl-10 pr-4 py-2.5 bg-transparent border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed outline-none"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 ml-1">Phone Number (Optional)</label>
              <div className="relative group">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => handleChange("contactPhone", e.target.value)}
                  placeholder="(613) 000-0000"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                />
              </div>
              {errors.contactPhone && (
                <p className="text-xs text-rose-500 mt-1 ml-1">{errors.contactPhone}</p>
              )}
            </div>
          </div>
        </div>

        <AudienceSelect value={audience} onChange={setAudience} />

        {errors.submit && (
          <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-3 text-rose-600">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">{errors.submit}</p>
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
